import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Button, Table, Form, Alert, Modal, Spinner, Badge } from 'react-bootstrap';
import { FaFilter, FaCalendarAlt, FaMoneyBillWave, FaUser, FaFileExcel } from 'react-icons/fa';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';


const SenetTakipPage = () => {
    const { currentUser } = useAuth();

    // State
    const [senetData, setSenetData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    // Filters
    const [filters, setFilters] = useState({
        customerName: '',
        startDate: '',
        endDate: '',
        paymentStatus: '',
        sortBy: 'contract',
        page: 1,
        limit: 20
    });

    // Dashboard Stats
    const [dashboardStats, setDashboardStats] = useState(null);

    useEffect(() => {
        loadSenetData();
    }, [filters]);

    // Load senet takip data
    const loadSenetData = async () => {
        try {
            setLoading(true);

            const params = new URLSearchParams();
            Object.entries(filters).forEach(([key, value]) => {
                if (value) params.append(key, value);
            });

            const response = await api.get(`/reports/senet-takip?${params}`);

            if (response.data.success) {
                const newData = response.data.data || [];
                setSenetData(newData);



                // Backend'den gelen stats'ları kullan
                if (response.data.stats) {

                    setDashboardStats(response.data.stats);
                } else {
                    console.log('❌ No stats in response'); // DEBUG
                }
            } else {
                setSenetData([]);
            }

        } catch (error) {
            setError('Senet takip verileri yüklenirken hata oluştu');
            console.error('❌ Senet takip error:', error);
        } finally {
            setLoading(false);
        }
    };

    // Update payment status
    const updatePaymentStatus = async (reportId, taksitNo, newStatus) => {
        try {
            // Backend'e gönder
            const response = await api.put(`/reports/senet-takip/${reportId}/${taksitNo}`, {
                durum: newStatus
            });

            setSuccess(`Taksit durumu "${newStatus}" olarak güncellendi`);

            // ✅ ÇÖZÜM: Eğer aktif filtre varsa ve yeni status filtreye uymuyorsa
            if (filters.paymentStatus && filters.paymentStatus !== newStatus) {
                // Kaydı listeden çıkar
                setSenetData(prevData =>
                    prevData.filter(item =>
                        !(item.reportId === reportId && item.taksitNo === taksitNo)
                    )
                );
            } else {
                // Filtre yoksa veya status filtreye uyuyorsa, state'i güncelle
                setSenetData(prevData =>
                    prevData.map(item =>
                        item.reportId === reportId && item.taksitNo === taksitNo
                            ? { ...item, durum: newStatus }
                            : item
                    )
                );
            }

        } catch (error) {
            console.error('❌ Senet status update error:', error);
            setError(error.response?.data?.message || 'Durum güncellenirken hata oluştu');

            // Hata varsa veriyi yenile
            loadSenetData();
        }
    };

    // Filter handlers
    const handleFilterChange = (field, value) => {
        console.log('🔍 Filter change:', { field, value });

        setFilters(prev => ({
            ...prev,
            [field]: value,
            page: field === 'page' ? value : 1
        }));
    };

    const clearFilters = () => {
        setFilters({
            customerName: '',
            startDate: '',
            endDate: '',
            paymentStatus: '',
            page: 1,
            limit: 20
        });
    };


    // Excel export handler
    const handleExcelExport = async () => {
        try {
            if (!filters.startDate || !filters.endDate) {
                setError('Lütfen başlangıç ve bitiş tarihlerini seçin');
                return;
            }

            setLoading(true);

            const response = await api.get('/reports/senet-takip/export-excel', {
                params: {
                    startDate: filters.startDate,
                    endDate: filters.endDate
                },
                responseType: 'blob'
            });

            const filename = `portfolio-report-${filters.startDate}-${filters.endDate}.xlsx`;

            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', filename);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);

            setSuccess(`Excel raporu başarıyla indirildi: ${filename}`);

        } catch (error) {
            console.error('❌ Excel export hatası:', error);
            setError(error.response?.data?.message || 'Excel dosyası oluşturulurken hata oluştu');
        } finally {
            setLoading(false);
        }
    };

    // Format currency
    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('tr-TR', {
            style: 'currency',
            currency: 'TRY'
        }).format(amount);
    };

    // Format date
    const formatDate = (dateStr) => {
        return new Date(dateStr).toLocaleDateString('tr-TR');
    };

    // Durum rengini belirle (pastel tonlar)
    const getDurumStyle = (durum) => {
        const styles = {
            'Tahsil': {
                backgroundColor: '#d4edda',
                color: '#155724',
                border: '1px solid #c3e6cb'
            },
            'Portföy': {
                backgroundColor: '#fff3cd',
                color: '#856404',
                border: '1px solid #ffeaa7'
            },
            'Erken Kapama': {
                backgroundColor: '#d1ecf1',
                color: '#0c5460',
                border: '1px solid #bee5eb'
            },
            'Yapılandırma': {
                backgroundColor: '#e7d4f5',
                color: '#5a3d70',
                border: '1px solid #d4b3e6'
            }
        };

        return styles[durum] || {};
    };

    return (
        <div className="senet-takip-page">
            <Container className="py-5">
                {/* Page Header */}
                <Row className="mb-4">
                    <Col md={12}>
                        <h1>📋 Senet Takip</h1>
                        <p className="text-muted">Tüm sözleşme taksitlerini takip edin ve durumlarını güncelleyin</p>
                    </Col>
                </Row>

                {/* Alerts */}
                {error && <Alert variant="danger" onClose={() => setError('')} dismissible>{error}</Alert>}
                {success && <Alert variant="success" onClose={() => setSuccess('')} dismissible>{success}</Alert>}

                {/* Dashboard Stats */}
                {dashboardStats && currentUser?.userType === 'admin' && (
                    <Row className="mb-4">
                        <Col md={3}>
                            <Card className="text-center">
                                <Card.Body>
                                    <h3>{dashboardStats.toplamSenetSayisi}</h3>
                                    <small>Toplam Senet Sayısı</small>
                                </Card.Body>
                            </Card>
                        </Col>
                        <Col md={3}>
                            <Card className="text-center">
                                <Card.Body>
                                    <h5>{formatCurrency(dashboardStats.toplamSenetMiktari)}</h5>
                                    <small>Toplam Senet Tutarı</small>
                                </Card.Body>
                            </Card>
                        </Col>
                        <Col md={3}>
                            <Card className="text-center">
                                <Card.Body>
                                    <h3 className="text-success">{dashboardStats.tahsilEdilen}</h3>
                                    <small>Tahsil Edilen</small>
                                </Card.Body>
                            </Card>
                        </Col>
                        <Col md={3}>
                            <Card className="text-center">
                                <Card.Body>
                                    <h3 className="text-warning">{dashboardStats.portfoydeKalan}</h3>
                                    <small>Portföyde Kalan</small>
                                </Card.Body>
                            </Card>
                        </Col>

                    </Row>
                )}

                {/* Filters */}
                <Card className="mb-4">
                    <Card.Header>
                        <FaFilter className="me-2" />Filtreler
                    </Card.Header>
                    <Card.Body>
                        <Row>
                            <Col md={3}>
                                <Form.Control
                                    type="text"
                                    placeholder="Müşteri adı ara..."
                                    value={filters.customerName}
                                    onChange={(e) => handleFilterChange('customerName', e.target.value)}
                                />
                            </Col>
                            <Col md={2}>
                                <Form.Select
                                    value={filters.paymentStatus}
                                    onChange={(e) => handleFilterChange('paymentStatus', e.target.value)}
                                >
                                    <option value="">Tüm Durumlar</option>
                                    <option value="Tahsil">Tahsil Edilen</option>
                                    <option value="Portföy">Portföyde Kalan</option>
                                    <option value="Erken Kapama">Erken Kapama</option>
                                    <option value="Yapılandırma">Yapılandırma</option>
                                </Form.Select>
                            </Col>
                            <Col md={2}>
                                <Form.Select
                                    value={filters.sortBy}
                                    onChange={(e) => handleFilterChange('sortBy', e.target.value)}
                                >
                                    <option value="contract">Sözleşme Sırası</option>
                                    <option value="date">Tarih Sırası</option>
                                </Form.Select>
                            </Col>
                            <Col md={2}>
                                <Form.Control
                                    type="date"
                                    value={filters.startDate}
                                    onChange={(e) => handleFilterChange('startDate', e.target.value)}
                                />
                            </Col>
                            <Col md={2}>
                                <Form.Control
                                    type="date"
                                    value={filters.endDate}
                                    onChange={(e) => handleFilterChange('endDate', e.target.value)}
                                />
                            </Col>
                            <Col md={2}>
                                <Button
                                    variant="dark"
                                    onClick={clearFilters}
                                    className="w-100"
                                    style={{ height: '38px' }}
                                >
                                    Temizle
                                </Button>
                            </Col>
                        </Row>
                        {/* Excel Export Butonu */}
                        <Row className="mt-3">
                            <Col md={12} className="d-flex justify-content-end">
                                <Button
                                    variant="success"
                                    onClick={handleExcelExport}
                                    disabled={!filters.startDate || !filters.endDate || loading}
                                    style={{ minWidth: '200px' }}
                                >
                                    <FaFileExcel className="me-2" />
                                    Portföy Raporu İndir (Excel)
                                </Button>
                            </Col>
                        </Row>
                    </Card.Body>
                </Card>

                {/* Data Table */}
                <Card>
                    <Card.Header>
                        <h5>Senet Taksitleri</h5>
                    </Card.Header>
                    <Card.Body>
                        {loading ? (
                            <div className="text-center">
                                <Spinner animation="border" />
                                <p>Yükleniyor...</p>
                            </div>
                        ) : (
                            <div className="table-responsive">
                                <Table hover className="mb-0">
                                    <thead>
                                        <tr>
                                            <th>Müşteri</th>
                                            <th>Taksit</th>
                                            <th>Tarih</th>
                                            <th>Tutar</th>
                                            <th>Durum</th>
                                            <th>Araç</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {senetData.length > 0 ? (
                                            senetData.map(item => (
                                                <tr key={item._id}>
                                                    <td>
                                                        <div>
                                                            <strong>{item.customerName}</strong>
                                                            <br />
                                                            <small className="text-muted">{item.customerIdNumber}</small>
                                                        </div>
                                                    </td>
                                                    <td>
                                                        {item.taksitNo}/{item.totalTaksit}
                                                    </td>
                                                    <td>
                                                        <FaCalendarAlt className="me-1" />
                                                        {formatDate(item.taksitTarihi)}
                                                    </td>
                                                    <td>

                                                        {formatCurrency(item.taksitTutari)}
                                                    </td>
                                                    <td>
                                                        <Form.Select
                                                            value={item.durum}
                                                            onChange={(e) => updatePaymentStatus(item.reportId, item.taksitNo, e.target.value)}
                                                            style={{
                                                                width: '150px',
                                                                fontWeight: '500',
                                                                ...getDurumStyle(item.durum)
                                                            }}
                                                        >
                                                            <option value="Portföy">Portföy</option>
                                                            <option value="Tahsil">Tahsil</option>
                                                            <option value="Erken Kapama">Erken Kapama</option>
                                                            <option value="Yapılandırma">Yapılandırma</option>

                                                        </Form.Select>
                                                    </td>
                                                    <td>
                                                        <div>
                                                            <strong>{item.vehicleInfo}</strong>
                                                            <br />
                                                            <small className="text-muted">{item.vehiclePlate}</small>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))
                                        ) : (
                                            <tr>
                                                <td colSpan="6" className="text-center py-4">
                                                    {filters.customerName || filters.paymentStatus || filters.startDate || filters.endDate
                                                        ? 'Filtrelere uygun veri bulunamadı'
                                                        : 'Henüz senet taksiti bulunamadı'
                                                    }
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </Table>
                            </div>
                        )}

                        {/* Pagination */}
                        {senetData.length > 0 && (
                            <div className="d-flex justify-content-between align-items-center mt-3">
                                <small className="text-muted">
                                    Gösterilen: {senetData.length} taksit
                                </small>
                                <div className="d-flex align-items-center gap-2">
                                    <Button
                                        variant="outline-secondary"
                                        size="sm"
                                        onClick={() => handleFilterChange('page', Math.max(filters.page - 1, 1))}
                                        disabled={filters.page <= 1}
                                    >
                                        « Önceki
                                    </Button>
                                    <Form.Select
                                        size="sm"
                                        value={filters.page}
                                        onChange={(e) => handleFilterChange('page', parseInt(e.target.value))}
                                        style={{ width: 'auto', minWidth: '140px' }}
                                    >
                                        {dashboardStats && Array.from({ length: Math.ceil(dashboardStats.toplamSenetSayisi / filters.limit) || 1 }, (_, i) => i + 1).map(pageNum => (
                                            <option key={pageNum} value={pageNum}>
                                                Sayfa {pageNum}/{Math.ceil(dashboardStats.toplamSenetSayisi / filters.limit) || 1}
                                            </option>
                                        ))}
                                    </Form.Select>
                                    <Button
                                        variant="outline-secondary"
                                        size="sm"
                                        onClick={() => handleFilterChange('page', filters.page + 1)}
                                        disabled={senetData.length < filters.limit}
                                    >
                                        Sonraki »
                                    </Button>
                                </div>
                            </div>
                        )}
                    </Card.Body>
                </Card>
            </Container>
        </div>
    );
};

export default SenetTakipPage;